import { Box, Flex, Image, Stack } from '@chakra-ui/react';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useNavigate, useLocation } from 'react-router';
import { formatDateTime } from '@/utils';
import { useErrorStore } from '@/store';
import { getModuleName } from '@/utils/errorUtils';

const ErrorBoundary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { error: errorInfo } = useErrorStore();

  const goBack = () => {
    navigate(-1);
  };

  // Use pathname from error info if available, otherwise use current location
  const errorPathname = errorInfo?.pathname || location.pathname;
  const moduleName = getModuleName(errorPathname);
  return (
    <Box mt={{ base: 20, md: 20, lg: 40 }} px={{ base: 10, md: 20, lg: 40 }}>
      <Flex
        direction={{ base: 'column', md: 'column', lg: 'row' }}
        gap={{ base: 8, md: 4 }}
        align={{ base: 'center', md: 'center', lg: 'flex-start' }}
      >
        <Flex
          flex={{ base: 'none', md: '0 0 28.57%', lg: '0 0 28.57%' }}
          justifyContent={{ base: 'center', md: 'center' }}
          alignItems={{ base: 'center', md: 'center', lg: 'flex-start' }}
          w={{ base: '50%', md: '50%', lg: 'auto' }}
        >
          <Image src="/svgs/robot-sad.svg" />
        </Flex>
        <Flex
          flex={{ base: 'none', md: '1 1 71.43%', lg: '1 1 71.43%' }}
          direction="column"
          gap={{ base: 4, md: 4 }}
          w={{ base: '100%', md: 'auto' }}
          maxW="100%"
        >
            <FaceliftText fontSize={{ base: '2xl', md: '4xl' }} fontWeight={'bold'} wordBreak="break-word">
              Something went wrong :(
            </FaceliftText>
            <FaceliftText
              fontStyle={'italic'}
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight={'bold'}
              wordBreak="break-word"
            >
              {"We're sorry — an unexpected error occurred while loading this page"}
            </FaceliftText>
            <FaceliftText wordBreak="break-word">
              {`Our system encountered an issue and couldn't complete your
              request. Please try refreshing the page, or return to the
              dashboard. If the problem persists, contact the support team with
              the error details below.`}
            </FaceliftText>

            <Box
              border={'1px solid'}
              borderColor={'gray.300'}
              borderRadius={8}
              bg={{ base: '#F3FFFD', _dark: '#21233c' }}
              p={4}
              maxW="100%"
              overflow="hidden"
            >
              <FaceliftText fontStyle="italic" color={{ base: 'gray.900', _dark: 'gray.300' }} wordBreak="break-word">
                <FaceliftText as="span" fontWeight={'bold'}>
                  Error Summary:{' '}
                </FaceliftText>{' '}
                {errorInfo?.name && `${errorInfo.name}: `}
                {errorInfo?.message || 'An unexpected error occurred'}
              </FaceliftText>
              <Box display="flex" gap={2} fontStyle="italic" color={{ base: 'gray.900', _dark: 'gray.300' }}>
                <FaceliftText as="span" fontWeight={'bold'}>
                  Module Affected:
                </FaceliftText>
                <FaceliftText>
                  {moduleName}
                </FaceliftText>
              </Box>
              <Box display="flex" gap={2} fontStyle="italic" color={{ base: 'gray.900', _dark: 'gray.300' }}>
                <FaceliftText as={'span'} fontWeight={'bold'}>
                  Timestamp:
                </FaceliftText>
                <FaceliftText>
                  {errorInfo?.timestamp 
                    ? formatDateTime(new Date(errorInfo.timestamp))
                    : formatDateTime(new Date())
                  }
                </FaceliftText>
              </Box>
            </Box>

            <Stack direction={{ base: 'column', md: 'row', lg: 'row' }} gap={{ base: 2, md: 2 }}>
              <FaceliftButton
                bg={'transparent'}
                width={{ base: '100%', md: '32%' }}
                size={'md'}
                color={'inherit'}
                border={'1px solid'}
                onClick={() => {
                  goBack();
                }}
              >
                Refresh Page
              </FaceliftButton>
              <FaceliftButton
                bg={'transparent'}
                width={{ base: '100%', md: '32%' }}
                size={'md'}
                color={'inherit'}
                border={'1px solid'}
                onClick={() => {
                  navigate('/benefits-verifications');
                }}
              >
                Go to Dashboard
              </FaceliftButton>
              <FaceliftButton
                size={'md'}
                width={{ base: '100%', md: '32%' }}
                onClick={() => {
                  navigate('/support');
                }}
              >
                Contact Support
              </FaceliftButton>
            </Stack>
        </Flex>
      </Flex>
    </Box>
  );
};

export default ErrorBoundary;
